import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { type Dayjs } from "dayjs";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DateTimePicker,
  EmptyState,
  FormModal,
  FormSectionLabel,
  Input,
  Page,
  Select,
  Table,
  Text,
  Textarea,
  type Column,
} from "@marekl/adminer/components";
import { useConfirm, useTableSort } from "@marekl/adminer/hooks";
import { formatDateTime } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { NoticeText } from "../../../components/Notices";
import { AudienceFields, useAudienceLabel } from "../../../components/AudienceFields";
import type { SystemRole } from "../../../auth/queries";
import type { ID } from "../../../lib/id";
import {
  useAllNotices,
  useDeleteNotice,
  useSaveNotice,
  type Notice,
  type NoticeAudience,
  type NoticeDisplay,
  type NoticeSeverity,
} from "../../../lib/queries/notices";
import {
  NOTICE_AUDIENCE_LABELS,
  NOTICE_DISPLAY_LABELS,
  NOTICE_SEVERITY_LABELS,
  NOTICE_STATUS_LABELS,
  NOTICE_STATUS_VARIANT,
  noticeStatus,
} from "../../../lib/noticeState";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

interface FormState {
  title: string;
  body: string;
  severity: NoticeSeverity;
  display: NoticeDisplay;
  audience: NoticeAudience;
  dismissible: boolean;
  starts_at: Dayjs | null;
  ends_at: Dayjs | null;
  roles: SystemRole[];
  team_ids: ID[];
  federation_ids: ID[];
}

const EMPTY: FormState = {
  title: "",
  body: "",
  severity: "info",
  display: "banner",
  audience: "signed_in",
  dismissible: true,
  starts_at: null,
  ends_at: null,
  roles: [],
  team_ids: [],
  federation_ids: [],
};

type ModalState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; notice: Notice };

function windowLabel(n: Notice): string {
  if (!n.starts_at && !n.ends_at) return "bez omezení";
  if (!n.ends_at) return `od ${formatDateTime(n.starts_at)}`;
  if (!n.starts_at) return `do ${formatDateTime(n.ends_at)}`;
  return `${formatDateTime(n.starts_at)} až ${formatDateTime(n.ends_at)}`;
}

export function NoticesPage() {
  const { data: me } = useMe();
  // Čtení všeho reads every notice, drafts included, and changes none.
  const readOnly = readsOnlyAsGlobalViewer(me, "notices_manager");
  const { data: notices = [], isLoading, error, refetch } = useAllNotices();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [form, setForm] = useState<FormState>(EMPTY);
  const confirm = useConfirm();
  // Re-render once a minute so „Stav" moves with the clock on a page left open.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const { mutateAsync: deleteNotice } = useDeleteNotice();
  const editing = modal.kind === "edit" ? modal.notice : null;
  const save = useSaveNotice(editing?.id ?? null);

  const targetsLabel = useAudienceLabel();

  // Who a notice reaches, in one line. The server decides it; this only
  // reads back what was written.
  const audienceLabel = useCallback(
    (n: Notice): string =>
      n.audience !== "signed_in" ? "všichni" : targetsLabel(n) || "všichni přihlášení",
    [targetsLabel],
  );

  const close = useCallback(() => {
    setModal({ kind: "closed" });
    save.reset();
  }, [save]);

  const openCreate = () => {
    setForm(EMPTY);
    setModal({ kind: "create" });
  };

  const openEdit = useCallback((n: Notice) => {
    setForm({
      title: n.title,
      body: n.body,
      severity: n.severity,
      display: n.display,
      audience: n.audience,
      dismissible: n.dismissible,
      starts_at: n.starts_at,
      ends_at: n.ends_at,
      roles: n.roles,
      team_ids: n.team_ids,
      federation_ids: n.federation_ids,
    });
    setModal({ kind: "edit", notice: n });
  }, []);

  const targeted = form.audience === "signed_in";
  const submit = () => {
    save.mutate(
      {
        title: form.title,
        body: form.body,
        severity: form.severity,
        display: form.display,
        audience: form.audience,
        dismissible: form.dismissible,
        starts_at: form.starts_at?.toISOString() ?? null,
        ends_at: form.ends_at?.toISOString() ?? null,
        // Targets narrow the app side only; the server refuses them anywhere
        // else, so a switch of „Kde" drops them rather than failing.
        roles: targeted ? form.roles : [],
        team_ids: targeted ? form.team_ids : [],
        federation_ids: targeted ? form.federation_ids : [],
      },
      { onSuccess: close },
    );
  };

  const windowError =
    form.starts_at && form.ends_at && !form.ends_at.isAfter(form.starts_at)
      ? "Konec musí být po začátku"
      : undefined;
  const canSubmit = form.title.trim().length > 0 && !windowError;

  const askDelete = useCallback(
    (n: Notice) =>
      confirm({
        title: "Smazat oznámení",
        variant: "danger",
        description: (
          <>
            Smazat oznámení <strong>{n.title}</strong>? Zmizí všem, kdo ho právě vidí.
          </>
        ),
        confirmLabel: "Smazat",
        onConfirm: async () => {
          await deleteNotice(n.id);
        },
      }),
    [confirm, deleteNotice],
  );

  // The status is read against the clock at each render, never one
  // captured when the columns were built: a page left open past a
  // notice's start must not keep calling it „Naplánováno".
  const columns = useMemo<Column<Notice>[]>(() => {
    return [
      {
        key: "title",
        header: "Nadpis",
        primary: true,
        sortable: true,
        sortValue: (n) => n.title,
        render: (n) => n.title,
      },
      {
        key: "display",
        header: "Zobrazení",
        sortable: true,
        sortValue: (n) => NOTICE_DISPLAY_LABELS[n.display],
        render: (n) => (
          <>
            {NOTICE_DISPLAY_LABELS[n.display]}{" "}
            <Badge variant={n.severity}>{NOTICE_SEVERITY_LABELS[n.severity]}</Badge>
          </>
        ),
      },
      {
        key: "audience",
        header: "Kde",
        sortable: true,
        sortValue: (n) => NOTICE_AUDIENCE_LABELS[n.audience],
        render: (n) => NOTICE_AUDIENCE_LABELS[n.audience],
      },
      {
        key: "who",
        header: "Komu",
        sortable: true,
        sortValue: (n) => audienceLabel(n),
        render: (n) => audienceLabel(n),
      },
      {
        key: "window",
        header: "Kdy",
        sortable: true,
        // A notice with only an end sorts by it, not last.
        sortValue: (n) => (n.starts_at ?? n.ends_at)?.toISOString() ?? null,
        render: (n) => <Text variant="muted">{windowLabel(n)}</Text>,
      },
      {
        key: "status",
        header: "Stav",
        sortable: true,
        sortValue: (n) => NOTICE_STATUS_LABELS[noticeStatus(n, dayjs())],
        render: (n) => {
          const s = noticeStatus(n, dayjs());
          return <Badge variant={NOTICE_STATUS_VARIANT[s]}>{NOTICE_STATUS_LABELS[s]}</Badge>;
        },
      },
      {
        key: "created",
        header: "Vytvořeno",
        sortable: true,
        sortValue: (n) => n.created_at.toISOString(),
        render: (n) => <Text variant="muted">{formatDateTime(n.created_at)}</Text>,
      },
      {
        key: "actions",
        header: "",
        render: (n) => readOnly ? null : (
          <Table.Actions>
            <Table.Action appearance="button" onClick={() => openEdit(n)}>
              Upravit
            </Table.Action>
            <Table.Action appearance="button" variant="danger" onClick={() => askDelete(n)}>
              Smazat
            </Table.Action>
          </Table.Actions>
        ),
      },
    ];
  }, [openEdit, askDelete, audienceLabel, readOnly]);

  const tableSort = useTableSort(notices, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "created", dir: "desc" },
  });

  return (
    <>
      <AppTopbar crumbs={[{ label: "Oznámení" }]} />
      <Page>
        <Page.Header
          title="Oznámení"
          subtitle="oznámení pro uživatele na přihlašovací stránce a v aplikaci"
          actions={
            !readOnly && (
              <Button variant="primary" onClick={openCreate}>
                Nové oznámení
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          <Table<Notice>
            framed
            {...tableSort.tableProps}
            rowKey={(n) => n.id}
            columns={columns}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            cards="sm"
            empty={
              <EmptyState
                title="Žádná oznámení"
                description="Zatím jste nikomu nic neoznámili."
              />
            }
          />
        </Page.Content>
      </Page>

      <FormModal
        open={modal.kind !== "closed"}
        title={modal.kind === "edit" ? "Upravit oznámení" : "Nové oznámení"}
        submitLabel={modal.kind === "edit" ? "Uložit" : "Vytvořit"}
        busy={save.isPending}
        canSubmit={canSubmit}
        error={save.error}
        onSubmit={submit}
        onClose={close}
        width={640}
      >
        <Input
          label="Nadpis"
          required
          maxLength={191}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <Textarea
          label="Text"
          rows={5}
          maxLength={4000}
          value={form.body}
          hint="Prostý text. Prázdný řádek začne nový odstavec, webová adresa se změní v odkaz."
          onChange={(e) => setForm({ ...form, body: e.target.value })}
        />
        <Select
          label="Zobrazení"
          value={form.display}
          hint={
            form.display === "popup"
              ? "Vyskakovací okno se ukazuje znovu, dokud ho uživatel nepotvrdí tlačítkem „Rozumím“. Potvrzení si pamatuje jeho prohlížeč."
              : "Lišta přes celou šířku pod horním řádkem stránky."
          }
          onChange={(e) => setForm({ ...form, display: e.target.value as NoticeDisplay })}
        >
          {(Object.keys(NOTICE_DISPLAY_LABELS) as NoticeDisplay[]).map((d) => (
            <option key={d} value={d}>
              {NOTICE_DISPLAY_LABELS[d]}
            </option>
          ))}
        </Select>
        {form.display === "banner" && (
          <Checkbox
            label="Uživatel může lištu skrýt"
            checked={form.dismissible}
            hint="Skrytá lišta se vrátí po dalším přihlášení. Na přihlašovací stránce skrýt nejde."
            onChange={(e) => setForm({ ...form, dismissible: e.target.checked })}
          />
        )}
        <Select
          label="Závažnost"
          value={form.severity}
          onChange={(e) => setForm({ ...form, severity: e.target.value as NoticeSeverity })}
        >
          {(Object.keys(NOTICE_SEVERITY_LABELS) as NoticeSeverity[]).map((s) => (
            <option key={s} value={s}>
              {NOTICE_SEVERITY_LABELS[s]}
            </option>
          ))}
        </Select>
        <Select
          label="Kde"
          value={form.audience}
          onChange={(e) => setForm({ ...form, audience: e.target.value as NoticeAudience })}
        >
          {(Object.keys(NOTICE_AUDIENCE_LABELS) as NoticeAudience[]).map((a) => (
            <option key={a} value={a}>
              {NOTICE_AUDIENCE_LABELS[a]}
            </option>
          ))}
        </Select>

        {targeted ? (
          <>
            <FormSectionLabel>Komu</FormSectionLabel>
            <AudienceFields
              value={form}
              onChange={(t) => setForm({ ...form, ...t })}
              hint="Oznámení uvidí ten, kdo má některou z rolí a zároveň patří do některého z týmů nebo federací. Globální role (např. Administrátor) patří do všech. Prázdné pole znamená všechny."
            />
          </>
        ) : (
          <Text variant="muted">
            Na přihlašovací stránce nikdo přihlášený není, takže oznámení vidí všichni.
          </Text>
        )}

        <FormSectionLabel>Kdy</FormSectionLabel>
        <DateTimePicker
          label="Zobrazit od"
          clearable
          value={form.starts_at}
          hint="Prázdné: hned po uložení."
          onChange={(starts_at) => setForm({ ...form, starts_at })}
        />
        <DateTimePicker
          label="Zobrazit do"
          clearable
          value={form.ends_at}
          error={windowError}
          hint="Prázdné: dokud oznámení nesmažete."
          onChange={(ends_at) => setForm({ ...form, ends_at })}
        />

        <FormSectionLabel>Náhled</FormSectionLabel>
        <Alert variant={form.severity} title={form.title || "Nadpis"}>
          <NoticeText text={form.body} />
        </Alert>
      </FormModal>
    </>
  );
}
